import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ErrorSummary, H1, P, scrollToFirstError, Wrapper } from '@ahmdigital/ui';
import { Formik } from 'formik';
import { isEmpty, pick } from 'lodash/fp';
import { useState } from 'react';
import styled from 'styled-components';

import { JourneyProductType } from '../../types';
import { useByocNavigationContext } from '../../context/byoc-navigation-context';
import AboutYouForm from './form';
import buildYourCoverActions from '../../../../actions/build-your-cover';
import constants from '../../../../ahm-constants';
import CustomerActions, { CustomerActionType } from '../../../../actions/customer';
import refinePriceSchema, { RefinePriceSchemaType } from '../../../refine-price/form/schema';
import selectCustomer from '../../../../selectors/select-customer';
import selectCustomerFormInitialValues from '../../../../components/pricing-drawer/selectors/select-customer-form-initial-values';
import submitRefinePrice from '../../../refine-price/utils/submit-refine-price';
import submitRefinePriceAnalytics from '../../../refine-price/utils/submit-refine-price-analytics';
import submitRefinePriceLead from '../../../refine-price/utils/submit-refine-price-lead';

const ErrorSummaryWrapper = styled.div`
  text-align: left;
`;

const SCROLL_OFFSET = 100;

const schemaForErrorSummary = {
  age: {
    linkPhrases: ['age'],
  },
  partnerAge: {
    linkPhrases: ['partner’s age'],
  },
};
const errorOrder = ['age', 'partnerAge'];

type Props = {
  actions: CustomerActionType;
};

const AboutYou = ({ actions }: Props) => {
  const [formikErrors, setFormikErrors] = useState({});
  const [formikTouched, setFormikTouched] = useState({});
  const { goToNextStep } = useByocNavigationContext();
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const initialValues = useSelector(selectCustomerFormInitialValues) as RefinePriceSchemaType;

  if (!customer.productType) {
    initialValues.productType = constants.PRODUCT_TYPE.PACKAGE;
  }
  return (
    <>
      <Wrapper size="small">
        {!isEmpty(formikErrors) && (
          <ErrorSummaryWrapper>
            <ErrorSummary
              schema={schemaForErrorSummary}
              errors={pick(errorOrder, formikErrors)}
              touched={formikTouched}
            />
          </ErrorSummaryWrapper>
        )}
        <H1 looksLike="h2">About you</H1>
        <P variant="marginBottomSpacer10">
          Feeling a bit shy? Tell us a bit about yourself, so we can find you a perfect match.
        </P>
      </Wrapper>
      <div>
        <Formik<RefinePriceSchemaType>
          enableReinitialize
          validationSchema={refinePriceSchema}
          initialValues={initialValues}
          onSubmit={(values: RefinePriceSchemaType) => {
            dispatch(buildYourCoverActions.setAddOnType(null));
            submitRefinePrice({ actions, customer, formValues: values, initialValues });
            submitRefinePriceAnalytics(values);
            submitRefinePriceLead({
              formValues: values,
              leadSourceType: constants.LEAD_SOURCE_TYPES.CHOOSE_YOUR_COVER,
            });
            goToNextStep({ journeyType: values.productType as JourneyProductType });
          }}
        >
          {({ errors, isSubmitting }) => {
            if (isSubmitting && !isEmpty(errors)) {
              scrollToFirstError({ offset: SCROLL_OFFSET });
            }

            return <AboutYouForm captureFormState={{ setFormikErrors, setFormikTouched }} />;
          }}
        </Formik>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
  },
});

export default connect(null, mapDispatchToProps)(AboutYou);
